/**
 * Friends Icon Component
 * Displays the friends icon with notification badges
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFriends } from './friendsContext';
import './FriendsIcon.css';

function FriendsIcon() {
  const { t } = useTranslation();
  const { 
    friends,
    unreadCount, 
    unreadBySender,
    requests, 
    pendingChallenges,
    openModalWithTab,
    openChat
  } = useFriends();

  // Only count received requests for badge (action needed)
  // Sent requests don't need action from user, so no badge
  const receivedRequestCount = requests?.received?.length || 0;
  const pendingChallengeCount = pendingChallenges?.received?.length || 0;

  const handleClick = () => {
    // Priority: unread messages > challenges > requests > friends
    if (unreadCount > 0 && unreadBySender && Object.keys(unreadBySender).length > 0) {
      // Find the first friend with unread messages
      // Sort by unread count (most unread first) for better UX
      const sendersWithUnread = Object.entries(unreadBySender)
        .map(([userId, count]) => ({ userId: Number(userId), count }))
        .sort((a, b) => b.count - a.count);
      
      // Find the friend object for the first sender
      const firstSenderId = sendersWithUnread[0]?.userId;
      const friendWithUnread = friends.find(f => f.id === firstSenderId);
      
      if (friendWithUnread) {
        openChat(friendWithUnread);
        return;
      }
    }
    
    // Fall back to tab preselection if no unread messages or friend not found
    if (pendingChallengeCount > 0) {
      openModalWithTab('challenges');
    } else if (receivedRequestCount > 0) {
      openModalWithTab('requests');
    } else {
      openModalWithTab('friends');
    }
  };

  return (
    <div className="friends-icon-container" onClick={handleClick} title={t('friends.title')}>
      {/* Two users icon */}
      <svg 
        className="friends-icon" 
        viewBox="0 0 24 24" 
        fill="currentColor"
        width="24" 
        height="24"
      >
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
      </svg>

      {/* Notification badges */}
      <div className="friends-badges">
        {/* Unread messages badge - mail icon */}
        {unreadCount > 0 && (
          <span className="friends-badge friends-badge-mail" title={t('friends.unreadMessages', { count: unreadCount })}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          </span>
        )}

        {/* Pending challenges badge - crossed swords */}
        {pendingChallengeCount > 0 && (
          <span className="friends-badge friends-badge-challenge" title={t('friends.pendingChallenges', { count: pendingChallengeCount })}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
              <path d="M6.92 5H5l6 6 1.5-1.5L6.92 5zM18 5h-1.92l-5.58 5.5 1.5 1.5L18 5zM3 19v-2l7-7 2 2-7 7H3zm18-2v2h-2l-7-7 2-2 7 7z"/>
            </svg>
          </span>
        )}

        {/* Pending friend requests badge - person+ */}
        {/* Only show badge for received requests (action needed), not sent requests */}
        {receivedRequestCount > 0 && (
          <span className="friends-badge friends-badge-request" title={t('friends.friendRequests', { count: receivedRequestCount })}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
            </svg>
          </span>
        )}
      </div>
    </div>
  );
}

export default FriendsIcon;

